import React, { useState } from "react";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { AiFillCloseCircle } from "react-icons/ai";
import axios from "../../../axios";
import * as url from "../../../utils/url";
import { BiX } from "react-icons/bi";
import { toastContainer } from "../../../utils/toast";
import Select from "react-select";
import {
  applyTrivvyTitleText,
  DIFFICULTY_LEVELS,
  DUPLICATE_LANGUAGE_HELP_TEXT,
  LANGUAGE_UNSET_VALUE,
  DUPLICATE_LANGUAGE_PLACEHOLDER,
  DUPLICATE_IN_PROGRESS_LABEL,
  DUPLICATE_LANGUAGE_SELECT_LABEL,
  DUPLICATE_NO_CHANGE_LABEL,
  getDuplicateLanguageOptions,
  getTrivvyTitleText,
  isTrivvyGeneratedFromCustomTemplates,
} from "../data";

const DIFFICULTY_OPTIONS = [
  { label: DUPLICATE_NO_CHANGE_LABEL, value: 0 },
  ...DIFFICULTY_LEVELS,
];

export default function DuplicateTriviaPopup({
  open,
  onClose,
  item,
  afterDuplicateSuccess,
  isMessaging,
  isKc,
  // Same gate the Create modal applies. The server enforces it too; this only
  // stops offering a control the org's plan cannot use.
  canTranslate = false
}) {
  // Skips the prefix if it's already there, so re-duplicating a Trivvy still
  // titled "Copy of ..." doesn't stack a second one on top.
  const [duplicateTrivvyName, setDuplicateTrivvyName] = useState(() => {
    const sourceTitle = getTrivvyTitleText(item);
    return /^copy of /i.test(sourceTitle)
      ? sourceTitle
      : `Copy of ${sourceTitle}`;
  });

  const [difficultyLevel, setDifficultyLevel] = useState(0);

  // Holds the raw code, matching how every other react-select in this app is
  // driven; DUPLICATE_LANGUAGE_NO_CHANGE_VALUE ("") is the "No change" row.
  // English by default, matching the create modals. The list excludes the
  // source's own language, so this is always a real change.
  const [duplicateLanguage, setDuplicateLanguage] = useState(LANGUAGE_UNSET_VALUE);

  // Blocks a second POST while the first is in flight: duplicating regenerates
  // and translates, so the round trip is long enough to double-click through.
  const [isDuplicating, setIsDuplicating] = useState(false);

  const sourceLanguage = item && item.trivvyLanguage ? item.trivvyLanguage : "";

  // No "No change" row: the language is always an explicit choice. The list
  // still excludes the source's own language, so every option is a real change.
  const languageOptions = getDuplicateLanguageOptions(sourceLanguage);

  const closeIfIdle = () => {
    if (isDuplicating) {
      return;
    }
    onClose();
  };

  const duplicateTrivvy = () => {
    if (isDuplicating) {
      return;
    }

    setIsDuplicating(true);

    axios
      .post(url.duplicateTrivia, {
        currentTriviaId: item._id,
        // Carries the source title's styling onto the copy, same as rename.
        newDuplicateTrivvyName: applyTrivvyTitleText(
          item.name,
          duplicateTrivvyName
        ),
        difficultyLevel,
        isMessaging: isMessaging || false,
        isKc: isKc || false,
        // Sent only when the picker was shown. An org whose plan hides it must
        // behave exactly as it does today, not silently get an English copy.
        ...(canTranslate ? { targetLanguage: duplicateLanguage } : {}),
      })
      .then(({ data }) => {
        afterDuplicateSuccess(data.data);
        setTimeout(() => {
          onClose();
        }, 1000);
      })
      .catch((err) => {
        setIsDuplicating(false);
        toastContainer("Error while duplicating Trivvy", "error");
      });
  };
  return (
    <Modal isOpen={open} size="sm" centered className="bwai-modal">
        <ModalHeader
          className="bwai_modal_header"
          close={
            <BiX
              className="cursor-pointer"
              color="gray"
              size={30}
              onClick={closeIfIdle}
            />
          }
        >
          <span className="bwai_title_block">
            <span className="bwai_title_text">Duplicate Trivvy</span>
          </span>
        </ModalHeader>

      <ModalBody className="p-3">
        {/* No top margin here: ModalBody's own p-3 supplies the 16px inset, so an
            extra mt-3 doubled it to 32px and left the modal top-heavy — obvious
            once the Question Difficulty block is gone on Surveys. */}
        <div>
          <p>
            Are you sure you want to duplicate{" "}
            <b>{getTrivvyTitleText(item)}</b>.
          </p>

          {/* Question Difficulty applies to Knowledge Checks only, so it is not
              offered when duplicating a Survey. Excluding SURVEY rather than
              requiring KNOWLEDGE_CHECK keeps older templates that have no
              triviaCategoryType behaving exactly as before. */}
          {item &&
          item.triviaType == 1 &&
          item.triviaCategoryType !== "SURVEY" &&
          !isTrivvyGeneratedFromCustomTemplates(item) ? (
            <div className="my-3">
              <Label className="bwai_label_caps">Question Difficulty</Label>
              <Select
                options={DIFFICULTY_OPTIONS}
                value={difficultyLevel}
                onChange={(e) => {
                  if (e) {
                    setDifficultyLevel(e.value);
                  } else {
                    setDifficultyLevel(0);
                  }
                }}
                placeholder={"Select difficulty level"}
                disabled={isDuplicating}
              />
            </div>
          ) : (
            ""
          )}

          {canTranslate && (
          <div className="my-3">
            <Label className="bwai_label_caps">
              {DUPLICATE_LANGUAGE_SELECT_LABEL}
            </Label>
            <Select
              options={languageOptions}
              value={duplicateLanguage}
              onChange={(e) => {
                if (e) {
                  setDuplicateLanguage(e.value);
                } else {
                  setDuplicateLanguage(LANGUAGE_UNSET_VALUE);
                }
              }}
              placeholder={DUPLICATE_LANGUAGE_PLACEHOLDER}
              disabled={isDuplicating}
            />
            <small className="form-text text-muted">
              {DUPLICATE_LANGUAGE_HELP_TEXT}
            </small>
          </div>
          )}

          {/* mb-4, not my-3: `.bwai-modal .modal-body` sets padding-bottom to 0,
              so the last block's own margin is the only gap above the footer and
              16px read as flush against its top border. */}
          <div className="mt-3 mb-4">
            <Label className="bwai_label_caps">
                Please set new duplicate Trivvy name
              </Label>
            <Input
              value={duplicateTrivvyName}
              placeholder="Enter new duplicate Trivvy name"
              onChange={(e) => {
                setDuplicateTrivvyName(e.target.value);
              }}
              disabled={isDuplicating}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="bwai_popup_footer">
        <Button
          className="bwai_btn_outline"
          onClick={closeIfIdle}
          disabled={isDuplicating}
        >
          Cancel
        </Button>
        <Button
          onClick={duplicateTrivvy}
          className="bwai_btn_black"
          disabled={isDuplicating}
        >
          {isDuplicating ? DUPLICATE_IN_PROGRESS_LABEL : "Continue"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
